import { parsePtNumber } from '../utils/money';
import { isCycleCloseDue, isCycleCloseOverdue } from '../utils/cycleCloseAlert';
import { daysUntil, formatCycleEndDateLabel } from '../utils/selfiePeriod';
import { computeGrowthStreak } from './patrimonioComposition';
import { toNumber } from '../utils/number';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export function monthShortLabel(month) {
  return MONTHS[(month || 1) - 1] || String(month);
}

export function monthLongLabel(month) {
  if (!month) return '';
  const date = new Date(2000, (month || 1) - 1, 1);
  return new Intl.DateTimeFormat('pt-PT', { month: 'long' }).format(date);
}

/**
 * Mini bar chart data for selfie journey sparkline (shared with dashboard hero).
 */
export function buildSparklineBars(timeline = [], maxBars = 6) {
  const rows = (timeline || []).slice(-maxBars);
  if (!rows.length) {
    return { bars: [], labels: [], startLabel: '', endLabel: '' };
  }

  const values = rows.map((row) => toNumber(row.net_worth_eur));
  const max = Math.max(...values, 1);
  const bars = rows.map((row, index) => ({
    heightPct: Math.max(12, Math.round((toNumber(row.net_worth_eur) / max) * 88)),
    isLatest: index === values.length - 1,
    label: monthShortLabel(row.reference_month),
  }));

  return {
    bars,
    labels: rows.map((row) => monthShortLabel(row.reference_month)),
    startLabel: monthShortLabel(rows[0].reference_month),
    endLabel: monthShortLabel(rows[rows.length - 1].reference_month),
  };
}

/**
 * Mirrors backend `_compute_balance_eur` for live património preview.
 */
export function computeAccountBalanceEur(account, entry = {}, previousEntry = {}) {
  const native = parsePtNumber(String(entry.balance_native ?? '').trim());
  if (native === null) return 0;

  const currency = (account.currency || 'EUR').toUpperCase();
  if (currency === 'EUR') {
    return native;
  }

  const ppu = parsePtNumber(String(entry.price_per_unit ?? '').trim());
  if (ppu !== null) {
    return native * ppu;
  }

  const prevPpu = previousEntry?.price_per_unit;
  if (prevPpu != null && entry?.source === 'unchanged') {
    return native * toNumber(prevPpu);
  }

  const fx = parsePtNumber(String(entry.fx_rate ?? '').trim());
  if (fx !== null) {
    return native * fx;
  }

  return native;
}

export function computeLiveParts({
  accounts = [],
  liabilities = [],
  accountEntries = {},
  liabilityEntries = {},
  previousAccountEntries = {},
}) {
  let assets = 0;
  for (const account of accounts) {
    const entry = accountEntries[account.id] || {};
    const previous = previousAccountEntries[account.id];
    if (!String(entry.balance_native ?? '').trim()) continue;
    assets += computeAccountBalanceEur(account, entry, previous);
  }

  let debt = 0;
  for (const liability of liabilities) {
    const entry = liabilityEntries[liability.id] || {};
    const balance = parsePtNumber(String(entry.remaining_balance_eur ?? '').trim());
    if (balance === null) continue;
    debt += balance;
  }

  return { assets, debt, net: assets - debt };
}

export function computeLiveNetWorth({
  accounts = [],
  liabilities = [],
  accountEntries = {},
  liabilityEntries = {},
  previousAccountEntries = {},
  previousLiabilityEntries = {},
}) {
  void previousLiabilityEntries;
  return computeLiveParts({
    accounts,
    liabilities,
    accountEntries,
    liabilityEntries,
    previousAccountEntries,
  }).net;
}

/**
 * Pontos do gráfico de linha "jornada" (histórico de selfies) + variação %
 * face ao primeiro ponto. `buildSparklineBars` (barras, dashboard) fica
 * intocado — usado por `DashboardNetWorthHero.jsx`, fora do âmbito desta
 * migração; este é um helper novo, scoped ao histórico de selfies.
 * .pen: a0lhr (ds/card/metric-sparkline · jornada)
 */
export function buildJourneyLinePoints(timeline = [], maxPoints = 6) {
  const rows = (timeline || []).slice(-maxPoints);
  if (!rows.length) {
    return { points: [], labels: [], latestValue: null, pctChange: null };
  }

  const values = rows.map((row) => toNumber(row.net_worth_eur));
  const points = values.map((value, i) => ({ i, value }));
  const labels = rows.map((row) => monthShortLabel(row.reference_month));
  const latestValue = values[values.length - 1];
  const firstValue = values[0];
  const pctChange =
    rows.length > 1 && firstValue !== 0
      ? ((latestValue - firstValue) / Math.abs(firstValue)) * 100
      : null;

  return { points, labels, latestValue, pctChange };
}

export function computeEntryDelta(currentValue, previousValue) {
  const current = parsePtNumber(String(currentValue ?? '').trim());
  const previous =
    previousValue === null || previousValue === undefined
      ? null
      : toNumber(previousValue);
  if (current === null || previous === null) return null;
  return current - previous;
}

/**
 * Formats a raw API decimal (ex: "3000.000000") into an editable pt-PT
 * string ("3000,00") for `NumericInput`. Crypto quantities keep up to 8
 * decimals (trimmed of trailing zeros) instead of being rounded to cents —
 * `Decimal` fields from the backend routinely carry 6 trailing zeros, which
 * would otherwise overflow the input and, pre-`parsePtNumber`-fix, silently
 * inflate the parsed value (see money.js).
 */
export function formatDecimalForInput(raw, { maxDecimals = 2 } = {}) {
  if (raw === undefined || raw === null || raw === '') return '';
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(raw);
  const fixed =
    maxDecimals > 2
      ? num.toFixed(maxDecimals).replace(/0+$/, '').replace(/\.$/, '.00')
      : num.toFixed(2);
  return fixed.replace('.', ',');
}

export function buildPrefilledAccountEntries(accounts = [], previousEntries = {}) {
  const entries = {};
  const accountIndex = new Map(accounts.map((account) => [account.id, account]));
  for (const account of accounts) {
    const previous = previousEntries[account.id];
    if (previous?.balance_native !== undefined && previous?.balance_native !== null) {
      const isCrypto = accountIndex.get(account.id)?.category === 'crypto';
      entries[account.id] = {
        balance_native: formatDecimalForInput(previous.balance_native, {
          maxDecimals: isCrypto ? 8 : 2,
        }),
        price_per_unit:
          previous.price_per_unit !== undefined && previous.price_per_unit !== null
            ? formatDecimalForInput(previous.price_per_unit)
            : '',
        source: 'unchanged',
      };
    } else {
      entries[account.id] = {
        balance_native: '',
        price_per_unit: '',
        source: 'manual',
      };
    }
  }
  return entries;
}

export function buildPrefilledLiabilityEntries(liabilities = [], previousEntries = {}) {
  const entries = {};
  for (const liability of liabilities) {
    const previous = previousEntries[liability.id];
    if (
      previous?.remaining_balance_eur !== undefined &&
      previous?.remaining_balance_eur !== null
    ) {
      entries[liability.id] = {
        remaining_balance_eur: formatDecimalForInput(previous.remaining_balance_eur),
      };
    } else {
      entries[liability.id] = { remaining_balance_eur: '' };
    }
  }
  return entries;
}

export function buildSnapshotSubmitPayload({
  accounts = [],
  liabilities = [],
  accountEntries = {},
  liabilityEntries = {},
  referenceYear,
  referenceMonth,
  snapshotDate,
  cycleStart,
}) {
  const entries = accounts
    .map((account) => {
      const entry = accountEntries[account.id];
      const native = parsePtNumber(String(entry?.balance_native ?? '').trim());
      if (native === null) return null;
      const ppuRaw = String(entry?.price_per_unit ?? '').trim();
      const ppu = ppuRaw ? parsePtNumber(ppuRaw) : null;
      return {
        account_id: account.id,
        balance_native: String(native),
        price_per_unit: ppu !== null ? String(ppu) : undefined,
        source: entry?.source || 'manual',
      };
    })
    .filter(Boolean);

  const liability_entries = liabilities
    .map((liability) => {
      const entry = liabilityEntries[liability.id];
      const balance = parsePtNumber(String(entry?.remaining_balance_eur ?? '').trim());
      if (balance === null) return null;
      return {
        liability_id: liability.id,
        remaining_balance_eur: String(balance),
      };
    })
    .filter(Boolean);

  return {
    reference_year: referenceYear,
    reference_month: referenceMonth,
    snapshot_date: snapshotDate,
    cycle_start: cycleStart,
    entries,
    liability_entries,
  };
}

export function resolveSelfieInviteState(currentCycle, referenceDate = new Date()) {
  if (!currentCycle?.isCurrent || !currentCycle?.end) {
    return { kind: 'unknown' };
  }

  if (isCycleCloseDue(currentCycle, referenceDate)) {
    const endDate = new Date(`${currentCycle.end}T12:00:00`);
    return {
      kind: isCycleCloseOverdue(currentCycle, referenceDate) ? 'overdue' : 'due',
      cycleEnd: currentCycle.end,
      referenceMonth: endDate.getMonth() + 1,
      referenceYear: endDate.getFullYear(),
    };
  }

  return {
    kind: 'upcoming',
    cycleEnd: currentCycle.end,
    nextLabel: formatCycleEndDateLabel(currentCycle.end),
    daysUntil: daysUntil(currentCycle.end, referenceDate),
  };
}

export function resolvePortraitDelta(snapshot) {
  const change = snapshot?.net_worth_change_eur;
  if (change === null || change === undefined) {
    return { kind: 'baseline' };
  }
  const n = toNumber(change);
  if (n > 0) return { kind: 'up', value: n };
  if (n < 0) return { kind: 'down', value: n };
  return { kind: 'flat', value: 0 };
}

export function formatRevelationCopy({
  snapshot,
  previousSnapshot,
  timeline = [],
}) {
  const current = toNumber(snapshot?.net_worth_eur);
  const previous = previousSnapshot ? toNumber(previousSnapshot.net_worth_eur) : null;
  const delta = snapshot?.net_worth_change_eur != null ? toNumber(snapshot.net_worth_change_eur) : null;
  const streak = computeGrowthStreak(timeline);
  const selfieCount = timeline.length;

  const first = timeline.length <= 1 || previous === null;

  if (first) {
    return {
      variant: 'first',
      current,
      streak: 0,
      selfieCount,
      totalGain: 0,
    };
  }

  const resolvedDelta = delta ?? current - previous;
  const totalGain =
    timeline.length >= 2
      ? current - toNumber(timeline[0]?.net_worth_eur)
      : resolvedDelta;

  if (resolvedDelta === 0) {
    return {
      variant: 'flat',
      previous,
      current,
      delta: 0,
      streak: 0,
      selfieCount,
      totalGain,
    };
  }

  const isUp = resolvedDelta > 0;

  return {
    variant: isUp ? 'up' : 'down',
    previous,
    current,
    delta: resolvedDelta,
    streak: isUp ? streak : 0,
    selfieCount,
    totalGain,
  };
}

export function buildRevelationChartPoints(timeline = [], snapshot) {
  const rows = [...(timeline || [])];
  const hasCurrent = rows.some((row) => row.id === snapshot?.id);
  if (snapshot && !hasCurrent) {
    rows.push(snapshot);
  }
  if (!rows.length) return [];

  const values = rows.map((row) => toNumber(row.net_worth_eur));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const width = 300;
  const height = 120;
  const padding = 10;
  const step = rows.length > 1 ? (width - padding * 2) / (rows.length - 1) : 0;

  return rows.map((row, index) => ({
    x: padding + step * index,
    y: height - padding - ((toNumber(row.net_worth_eur) - min) / range) * (height - padding * 2),
    isNew: row.id === snapshot?.id,
    isLatest: index === rows.length - 1,
  }));
}
